import React, { useState } from 'react';
import { Building2, TrendingDown, Truck, HeadphonesIcon, CheckCircle2, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { addEnquiry, businessInfo } from '../mock.js';
import { useToast } from '../hooks/use-toast.js';

const Wholesale = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    businessName: '',
    contactPerson: '',
    phone: '',
    email: '',
    city: '',
    businessType: '',
    requirements: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addEnquiry({
      type: 'wholesale',
      ...formData
    });
    toast({
      title: "Enquiry Submitted!",
      description: "We'll contact you within 24 hours for wholesale pricing.",
    });
    setFormData({
      businessName: '',
      contactPerson: '',
      phone: '',
      email: '',
      city: '',
      businessType: '',
      requirements: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const wholesaleAdvantages = [
    {
      icon: TrendingDown,
      title: 'Competitive Pricing',
      description: 'Volume-based pricing structure that improves your margins'
    },
    {
      icon: Truck,
      title: 'Consistent Supply',
      description: 'Reliable inventory and timely delivery for your business needs'
    },
    {
      icon: HeadphonesIcon,
      title: 'Dedicated Support',
      description: 'Direct point of contact for quick resolution of queries'
    },
    {
      icon: Building2,
      title: 'Partnership Approach',
      description: 'Long-term relationships built on trust and mutual growth'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Wholesale & Dealer <span className="text-[#FF6B7A]">Enquiries</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-4">
              Built for workshops, dealers, and fleet operators who need consistent supply
            </p>
            <p className="text-base text-gray-400 max-w-2xl mx-auto">
              Whether you're a service center, equipment dealer, or manage a fleet of machines, we provide wholesale pricing and reliable stock depth for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Who Should Enquire */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Who Should Enquire?</h2>
            <p className="text-lg text-gray-600">We work with businesses across the equipment and service ecosystem</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'Workshops & Service Centers', desc: 'Regular supply of parts for your customer repairs' },
              { title: 'Equipment Dealers', desc: 'Stock parts for after-sales support and service' },
              { title: 'Fleet Operators', desc: 'Bulk parts for in-house maintenance teams' },
              { title: 'Sub-Dealers', desc: 'Wholesale rates for retail distribution' },
              { title: 'Rental Companies', desc: 'Keep your rental fleet running with ready parts' },
              { title: 'Industrial Buyers', desc: 'Bulk procurement for factory maintenance' }
            ].map((customer, index) => (
              <Card key={index} className="border-2 hover:border-[#00C9FF] transition-all">
                <CardContent className="p-6">
                  <CheckCircle2 className="h-8 w-8 text-[#00C9FF] mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{customer.title}</h3>
                  <p className="text-sm text-gray-600">{customer.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Wholesale Advantages */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Partner With Us?</h2>
            <p className="text-lg text-gray-600">Business advantages you get as a wholesale customer</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {wholesaleAdvantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <Card key={index} className="text-center border-2 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-br from-[#00C9FF]/10 to-[#FF6B7A]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-[#00C9FF]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{advantage.title}</h3>
                    <p className="text-sm text-gray-600">{advantage.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Submit Wholesale Enquiry</h2>
              <p className="text-lg text-gray-600">Fill in your details and we'll get back with pricing and terms</p>
            </div>

            <Card className="border-2">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="businessName" className="text-base">Business Name *</Label>
                      <Input
                        id="businessName"
                        name="businessName"
                        type="text"
                        required
                        value={formData.businessName}
                        onChange={handleChange}
                        placeholder="Your company/firm name"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactPerson" className="text-base">Contact Person *</Label>
                      <Input
                        id="contactPerson"
                        name="contactPerson"
                        type="text"
                        required
                        value={formData.contactPerson}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-base">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="10-digit mobile number"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-base">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="city" className="text-base">City/Location *</Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Your city"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="businessType" className="text-base">Business Type *</Label>
                      <Input
                        id="businessType"
                        name="businessType"
                        type="text"
                        required
                        value={formData.businessType}
                        onChange={handleChange}
                        placeholder="e.g., Workshop, Dealer, Fleet Operator"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="requirements" className="text-base">Your Requirements *</Label>
                    <Textarea
                      id="requirements"
                      name="requirements"
                      required
                      value={formData.requirements}
                      onChange={handleChange}
                      placeholder="Tell us about the parts you regularly need, expected volumes, and any specific requirements..."
                      className="mt-2 min-h-32"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      type="submit"
                      size="lg"
                      className="flex-1 bg-[#FF6B7A] hover:bg-[#FF5A6A] text-white"
                    >
                      Submit Wholesale Enquiry
                    </Button>
                    <Button 
                      type="button"
                      size="lg"
                      onClick={() => {
                        const message = encodeURIComponent("Hi, I'm interested in wholesale pricing for spare parts.");
                        window.open(`https://wa.me/${businessInfo.whatsapp}?text=${message}`, '_blank');
                      }}
                      className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
                    >
                      <MessageCircle className="mr-2 h-5 w-5" />
                      WhatsApp Us
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <p className="text-center text-sm text-gray-600 mt-6">
              We'll review your enquiry and contact you within 24 hours with pricing and partnership details.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Wholesale;
